import React, { useState, useEffect } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { PLATFORMS_CONFIG } from "../configs/constant";
import Avatar from './Avatar';

const AccountSelector = ({ accounts, selectedIds, onToggle }) => {
    if (accounts.length === 0) {
        return (
            <div className="text-center py-10 border-2 border-dashed border-gray-200 rounded-2xl">
                <p className="text-sm text-gray-400">No accounts connected.</p>
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 gap-3">
            {accounts.filter(acc => acc.status === 'active').map((acc) => {
                const isSelected = selectedIds.includes(acc.id);
                const isExpired = acc.status === 'expired';
                const config = JSON.parse(acc.config);
                const platformInfo = PLATFORMS_CONFIG[acc.platform];
                return (
                    <div
                        key={acc.id}
                        onClick={() => !isExpired && onToggle(acc.id)}
                        className={`group flex items-center p-3 rounded-xl border-2 transition-all cursor-pointer ${isSelected
                            ? 'border-red-500 bg-red-50/50'
                            : 'border-gray-100 hover:border-gray-200 bg-white'
                            } ${isExpired ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        <div className="relative">
                            <Avatar src={acc.avatar_url || config.avatar} name={acc.account_name} className="w-10 h-10 border border-gray-100" />
                            <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-[10px] text-white border-2 border-white ${PLATFORMS_CONFIG[acc.platform]?.color || 'bg-gray-400'}`}>
                                <i className={`${PLATFORMS_CONFIG[acc.platform]?.icon || 'fa-share-alt'}`}></i>
                            </div>
                        </div>

                        <div className="ml-3 flex-1 min-w-0">
                            <p className="text-sm font-bold text-gray-900 truncate">{acc.account_name}</p>
                            <p className="text-[10px] text-gray-400 uppercase font-semibold">{acc.platform}</p>
                        </div>
                        <div className="mt-3 flex flex-col items-end">
                            {/* 能力网格：仅渲染支持项，向右靠拢 */}
                            <div className="flex gap-2 justify-end w-full">
                                {/* 1. 视频列 */}
                                {platformInfo?.supportVideo && (
                                    <div className="flex flex-col items-center min-w-[45px] p-1.5 bg-purple-50/50 rounded-lg border border-purple-100/30">
                                        <i className="fa-regular fa-circle-play text-[10px] text-purple-500 mb-0.5"></i>
                                        <span className="text-[10px] font-black text-purple-700">
                                            {platformInfo?.video.count || 1}
                                        </span>
                                        <span className="text-[7px] font-bold text-purple-400 uppercase tracking-tighter">Videos</span>
                                    </div>
                                )}
                                {/* 2. 图片列 */}
                                {platformInfo?.supportImage && (
                                    <div className="flex flex-col items-center min-w-[45px] p-1.5 bg-red-50/50 rounded-lg border border-red-100/30">
                                        <i className="fa-regular fa-image text-[10px] text-red-500 mb-0.5"></i>
                                        <span className="text-[10px] font-black text-red-700">
                                            {platformInfo?.image.count || 1}
                                        </span>
                                        <span className="text-[7px] font-bold text-red-400 uppercase tracking-tighter">Images</span>
                                    </div>
                                )}
                                {/* 3. 文字列 */}
                                <div className="flex flex-col items-center min-w-[45px] p-1.5 bg-gray-50/80 rounded-lg border border-gray-100/50">
                                    <i className="fa-solid fa-font text-[10px] text-gray-400 mb-0.5"></i>
                                    <span className="text-[10px] font-black text-gray-700">
                                        {platformInfo?.maxChars > 9999 ? `${Math.floor(platformInfo.maxChars / 1000)}k` : platformInfo?.maxChars || '∞'}
                                    </span>
                                    <span className="text-[7px] font-bold text-gray-400 uppercase tracking-tighter">Chars</span>
                                </div>
                            </div>

                            {/* 底部特殊状态提示：极小字号，右对齐 */}
                            {!platformInfo?.allowPureText && (
                                <div className="mt-1.5 flex items-center gap-1 opacity-80">
                                    <i className="fa-solid fa-circle-exclamation text-amber-600 text-[8px]"></i>
                                    <span className="text-[8px] font-black text-amber-700 uppercase tracking-widest">
                                        Media Required to Post
                                    </span>
                                </div>
                            )}
                        </div>
                        {isSelected && !isExpired && (
                            <CheckCircle2 className="text-red-500" size={20} />
                        )}
                        {isExpired && (
                            <span className="text-[10px] bg-red-100 text-red-500 px-1.5 py-0.5 rounded font-bold">EXPIRED</span>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default AccountSelector;
